import { Select, Space } from 'antd'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import type { Models } from '@wolf/api'
import { format } from '@wolf/dates'
import { useUrlGlobalFilterParams } from '../useUrlGlobalFilterParams'
import { trpc } from '../utils/trpc'

type Props = {
  salesGroupFilter: Models.SalesGroupFilterType
  setSalesPlanFilter: React.Dispatch<React.SetStateAction<Models.SalesGroupFilterType>>
}

export const SalesGroupPicker = memo(({
  salesGroupFilter,
  setSalesPlanFilter,
}: Props) => {
  const { changeSalesGroupParams, salesGroupFilterParams, sourceFilterParams } = useUrlGlobalFilterParams()

  const stableSourceId = useMemo(() => sourceFilterParams.serialId ?? -1, [sourceFilterParams.serialId])

  const salesGroupsQuery = trpc.salesGroups.getSalesGroups.useQuery(stableSourceId, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: stableSourceId > 0,
    keepPreviousData: true
  })

  const salesGroups = salesGroupsQuery.data

  useEffect(() => {
    if (salesGroupFilter.salesGroupId === -1) return

    if (
      !salesGroupFilterParams.salesGroupId ||
      !salesGroupFilterParams.fromDate ||
      !salesGroupFilterParams.toDate
    ) {
      changeSalesGroupParams({
        salesGroupId: salesGroupFilter.salesGroupId,
        replace: true,
      })
      return
    }

    if (salesGroupFilterParams.salesGroupId !== salesGroupFilter.salesGroupId) {
      setSalesPlanFilter({
        salesGroupId: salesGroupFilterParams.salesGroupId,
        fromDate: salesGroupFilterParams.fromDate,
        toDate: salesGroupFilterParams.toDate,
      })
    }
  }, [salesGroupFilter.salesGroupId, salesGroupFilterParams.salesGroupId, salesGroupFilterParams.fromDate, salesGroupFilterParams.toDate, changeSalesGroupParams, setSalesPlanFilter])

  // Memoize the options to prevent recreating on every render
  const selectOptions = useMemo(() => {
    if (salesGroupsQuery.isLoading) {
      return [{ label: 'Cargando...', value: -1, disabled: true }]
    }

    if (salesGroupsQuery.error) {
      return [{ label: 'Error al cargar', value: -1, disabled: true }]
    }

    if (!salesGroups || salesGroups.length === 0) {
      return [{ label: 'No hay datos disponibles', value: -1, disabled: true }]
    }

    return salesGroups.map((salesGroup) => ({
      label: `${salesGroup.salesGroupId} - ${format(salesGroup.fromDate, 'dd/MM/yyyy')} -> ${format(salesGroup.toDate, 'dd/MM/yyyy')}`,
      value: salesGroup.salesGroupId,
    }))
  }, [salesGroups, salesGroupsQuery.isLoading, salesGroupsQuery.error])

  // Memoize the onChange handler
  const handleSalesGroupChange = useCallback((value: number) => {

    if (value < 0) return

    const salesGroup = salesGroups?.find((sg) => sg.salesGroupId === value)

    const newSalesGroupFilter = {
      salesGroupId: value,
      fromDate: salesGroup?.fromDate || new Date(),
      toDate: salesGroup?.toDate || new Date(),
    }

    setSalesPlanFilter(newSalesGroupFilter)

    changeSalesGroupParams({
      salesGroupId: Number(value),
      replace: false
    })
  }, [salesGroups, setSalesPlanFilter, changeSalesGroupParams])

  return (
    <>
      Semana Wetaca:{' '}
      <Space>
        <Select
          value={salesGroupFilter.salesGroupId}
          size="small"
          onChange={handleSalesGroupChange}
          style={{ minWidth: '5.5rem' }}
          showSearch
          options={selectOptions}
        />
      </Space>
    </>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.salesGroupFilter.salesGroupId === nextProps.salesGroupFilter.salesGroupId &&
    prevProps.salesGroupFilter.fromDate === nextProps.salesGroupFilter.fromDate &&
    prevProps.salesGroupFilter.toDate === nextProps.salesGroupFilter.toDate
  )
})
